// Audio registry — one row per logical audio event. The thin client runtime
// (client/src/audio/) reads this; `audio-manifest.json` resolves each `asset`
// key to a served URL. See docs/design/18-audio.md.
//
// Convention: a sound's manifest key == its event id, so `asset` defaults to
// `id` and most rows only carry overrides. A missing manifest entry is a
// silent no-op (data-driven-deps), so the registry can list events whose files
// don't exist yet.

export type AudioBus = "music" | "ambience" | "sfx" | "ui" | "voice";

export interface SoundDef {
  id: string;
  bus: AudioBus;
  /** Manifest key. Defaults to `id`. Ignored when `variants` is set. */
  asset?: string;
  /** Manifest keys; one is chosen at random per play (e.g. alternating hits). */
  variants?: string[];
  /** Base gain 0..1 (default 1). Balancing lever — final mastering lives here, not baked per-file. */
  volume?: number;
  /** Looping bed (music / ambience / channel). */
  loop?: boolean;
  /** World-positioned: panned + distance-attenuated from the listener. */
  spatial?: boolean;
  /** ± semitones of random pitch jitter (applied via playbackRate) to break repetition. */
  pitchVariance?: number;
  /** Max simultaneous instances of this id (manager steals oldest beyond it). */
  maxConcurrent?: number;
  /** Minimum gap between plays of this id, ms (de-machine-guns rapid triggers). */
  cooldownMs?: number;
}

type O = Partial<SoundDef>;
const ui = (id: string, o: O = {}): SoundDef => ({ id, bus: "ui", ...o });
const spatial = (id: string, o: O = {}): SoundDef => ({ id, bus: "sfx", spatial: true, ...o });
const flat = (id: string, o: O = {}): SoundDef => ({ id, bus: "sfx", ...o });
const amb = (id: string, o: O = {}): SoundDef => ({ id, bus: "ambience", loop: true, ...o });
const music = (id: string, o: O = {}): SoundDef => ({ id, bus: "music", loop: true, ...o });
const creature = (fam: string, states: string[]): SoundDef[] =>
  states.map((s) => ({ id: `creature.${fam}.${s}`, bus: "sfx", spatial: true, pitchVariance: 0.4 }));

const DEFS: SoundDef[] = [
  // --- UI (flat) ---
  ui("ui.click"),
  ui("ui.purchase"),
  ui("ui.panel-open"),
  ui("ui.panel-close"),
  ui("ui.tab", { cooldownMs: 40 }),
  ui("ui.denied"),
  ui("ui.equip.armor"),
  ui("ui.equip.light"),
  ui("ui.levelup"),
  ui("ui.notify"),

  // --- Combat (spatial) ---
  spatial("combat.hit", { variants: ["combat.hit.a", "combat.hit.b"], pitchVariance: 0.6, maxConcurrent: 5, cooldownMs: 40 }),
  spatial("combat.crit", { maxConcurrent: 4 }),
  spatial("combat.hit-armor", { pitchVariance: 0.6, maxConcurrent: 5, cooldownMs: 40 }),
  spatial("combat.block", { maxConcurrent: 4 }),
  spatial("build.place"),
  spatial("build.hit", { cooldownMs: 60 }),
  spatial("build.destroyed"),

  // --- Gathering (spatial, per-swing) ---
  spatial("gather.chop", { pitchVariance: 0.8, cooldownMs: 120 }),
  spatial("gather.mine-stone", { pitchVariance: 0.8, cooldownMs: 120 }),
  spatial("gather.mine-gold", { pitchVariance: 0.8, cooldownMs: 120 }),
  spatial("gather.pick", { pitchVariance: 0.8, cooldownMs: 120 }),
  spatial("gather.tree-fall"),

  // --- Creatures (spatial; select = on-click, attack, death) ---
  ...creature("canine", ["select", "attack", "death"]),
  ...creature("bear", ["select", "attack", "death"]),
  ...creature("deer", ["select", "death"]),
  ...creature("sheep", ["select", "death"]),
  ...creature("fox", ["select", "attack", "death"]),
  ...creature("undead", ["select", "attack", "death"]),
  ...creature("boss", ["select", "attack", "death"]),

  // --- Ambience (loops) + day/night announces ---
  amb("ambience.forest-day"),
  amb("ambience.forest-night"),
  amb("ambience.water", { spatial: true }),
  music("announce.dawn", { loop: false, volume: 0.8 }),
  music("announce.dusk", { loop: false, volume: 0.8 }),

  // --- Music ---
  music("music.menu"),
  music("music.match-day"),    // in-match score bed, daytime (layered over forest ambience)
  music("music.match-night"),  // in-match score bed, night (crossfades with match-day)
  music("music.victory", { loop: false }),
  music("music.defeat", { loop: false }),

  // --- Survival cues (flat) ---
  flat("survival.frostbite"),
  flat("survival.eat"),
  flat("survival.warning"),

  // --- Ability casts (spatial at caster) ---
  spatial("cast.frost"),
  spatial("cast.nature"),
  spatial("cast.shadow"),
  spatial("cast.spirit"),
  spatial("cast.force"),
  spatial("cast.channel", { loop: true }),
  spatial("cast.toggle-on"),
  spatial("cast.toggle-off"),
  spatial("spell.black-hole"),
  spatial("spell.arcane-nova"),
];

/**
 * Post-build per-sound volume balance (0..1). The single mastering lever for
 * sounds that are too HOT relative to the mix — too-QUIET files are fixed at
 * encode time (ffmpeg gain) since a registry volume can't exceed 1. Keep tuning
 * here, not baked per-file, so it stays reversible. See docs/design/18-audio.md.
 */
const VOLUME_OVERRIDE: Record<string, number> = {
  "creature.bear.attack": 0.4,    // loudest SFX in the set (−15.7 dB) — pull below canine
  "ambience.forest-night": 0.35,  // night bed ran ~28 dB hotter than the day bed
};

export const SOUNDS: Record<string, SoundDef> = Object.fromEntries(
  DEFS.map((d) => {
    const vol = VOLUME_OVERRIDE[d.id];
    return [d.id, vol !== undefined ? { ...d, volume: vol } : d];
  }),
);

export function getSound(id: string): SoundDef | undefined {
  return SOUNDS[id];
}

/** Default per-bus mix (0..1). Player mixer UI is a later UI-phase addition. */
export const BUS_DEFAULT_VOLUME: Record<AudioBus, number> = {
  music: 0.13,
  ambience: 0.03,
  sfx: 0.43,    // "Effects" slider
  ui: 0.53,     // "Interface" slider
  voice: 0.5,
};

/** Cast-school sound per ability school (see 18-audio.md). */
export const CAST_SCHOOL_SOUND: Record<string, string> = {
  frost: "cast.frost",
  nature: "cast.nature",
  shadow: "cast.shadow",
  spirit: "cast.spirit",
  force: "cast.force",
};

/**
 * Per-ability one-shot cast sound (`castResolved` → this id). The 16 spells
 * map onto the 5 elemental cast archetypes by flavor (cast.frost/nature/shadow/
 * spirit/force) + two bespoke spells (BlackHole, ArcaneNova). This is a
 * design/flavor table, easy to retune — a missing entry is a silent cast.
 *
 * Deliberately EXCLUDES toggle + channel abilities (ShadowCloak, AdrenalineRush,
 * SageMode → driven by `toggleStarted/Stopped`; CelestialHymn → `channelStarted/
 * Stopped`) so `castResolved` doesn't double up on their dedicated broadcasts.
 */
export const ABILITY_CAST_SOUND: Record<string, string> = {
  MineralTap: "cast.nature",
  NaturesGrace: "cast.nature",
  PlantTree: "cast.nature",
  Clairvoyance: "cast.spirit",
  FrostArmor: "cast.frost",
  BlackHole: "spell.black-hole",
  Stance: "cast.force",
  Backstab: "cast.shadow",
  SiphonLife: "cast.shadow",
  NeutralizingWave: "cast.spirit",
  GuardianSpirit: "cast.spirit",
  Affliction: "cast.shadow",
  ArcaneNova: "spell.arcane-nova",
  Flurry: "cast.force",
  SpectralEdict: "cast.shadow",
  Warcry: "cast.force",
  PowerWordShield: "cast.spirit",
};

/**
 * Creature `CreatureType` → audio family (clips shared across kin, per
 * 18-audio.md). A type with no entry has no creature SFX (silent no-op).
 */
export const CREATURE_FAMILY: Record<string, string> = {
  Stag: "deer",
  Sheep: "sheep",
  Fox: "fox",
  Wolf: "canine",
  Lyote: "canine",
  Bear: "bear",
  Ghoul: "undead",
  UndeadKobold: "undead",
  Wight: "undead",
  Drekavac: "undead",
  KingFurbeastIV: "boss",
  TheOldMammoth: "bear",
};

/**
 * Resource-node `kind` → per-swing gather sound. Trees additionally play
 * `gather.tree-fall` on the felling swing (handled in the trigger, not here).
 * Unknown kinds → no gather SFX.
 */
export function gatherSoundForKind(kind: string): string | undefined {
  if (/tree|sapling|wood/i.test(kind)) return "gather.chop";
  if (/gold/i.test(kind)) return "gather.mine-gold";
  if (/stone|rock|ore/i.test(kind)) return "gather.mine-stone";
  if (/berry|bush|herb|plant|mushroom|reed/i.test(kind)) return "gather.pick";
  return undefined;
}

export function isTreeKind(kind: string): boolean {
  return /tree|sapling|wood/i.test(kind);
}
